/**
 * Control Center "Poslovni tokovi" — the synthetic whole-business orchestration view.
 *
 * This is a control/observation surface, not an autonomous business success claim.
 * It shows the server-owned catalogue, the exact project context, honest CRM/Zia
 * availability, real job steps/handoffs, the existing human approval link, the
 * synthetic receipt/readback and the human-owned next step.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError, formatMicros, Job } from "../api/client";
import {
  businessJobStatusLabel,
  businessRoleStatusLabel,
  businessStepLabel,
  parseBusinessCatalogueResponse,
  parseBusinessRunView,
  safeBusinessSourceUrl,
} from "./businessFlow";
import type { BusinessCatalogueResponse, BusinessRunView } from "../api/client";

export interface BusinessFlowLoader {
  getBusinessCatalogue(projectId: string): Promise<unknown>;
  listBusinessRuns(projectId: string): Promise<{ items: Job[] }>;
  startBusinessRun(
    projectId: string,
    input: { goal: string; scenario_id: "current_evidence" | "research_gap"; idempotency_key: string },
  ): Promise<Job>;
  getBusinessRun(projectId: string, jobId: string): Promise<{ job: Job; business: unknown }>;
  cancelJob(projectId: string, jobId: string): Promise<unknown>;
}

const NOT_CONNECTED = "Nije povezano";
const TECH_DETAILS = "Tehnički detalji";
const EXAMPLE_GOAL =
  "Provjeri koji hotel iz naše baze je najbolji kandidat za prvi razgovor i predloži jedan sljedeći korak.";

const TERMINAL = new Set(["succeeded", "failed", "cancelled", "killed", "needs_review"]);
const STATUS_BADGE: Record<string, string> = {
  pending: "border-border text-muted-foreground",
  running: "border-sky-500/40 bg-sky-500/10",
  succeeded: "border-emerald-500/40 bg-emerald-500/10",
  skipped: "border-amber-500/40 bg-amber-500/10",
  failed: "border-destructive/40 bg-destructive/10",
  rejected: "border-destructive/40 bg-destructive/10",
  invalidated: "border-amber-500/40 bg-amber-500/10",
  needs_review: "border-amber-500/40 bg-amber-500/10",
  cancelled: "border-border text-muted-foreground",
  killed: "border-destructive/40 bg-destructive/10",
};

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.messageHr : "Radnja nije uspjela.";
}

function statusClass(status: string | null | undefined): string {
  return STATUS_BADGE[status ?? ""] ?? "border-border text-muted-foreground";
}

const STEP_STATUS_LABEL: Record<string, string> = {
  pending: "čeka",
  running: "u tijeku",
  succeeded: "završeno",
  skipped: "preskočeno",
  failed: "neuspjelo",
  rejected: "odbijeno",
  invalidated: "poništeno",
  needs_review: "za pregled",
  cancelled: "otkazano",
  killed: "zaustavljeno",
};

function stepStatusLabel(status: string | null | undefined): string {
  if (!status) return "";
  return STEP_STATUS_LABEL[status] ?? status;
}

function SummaryList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
      <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
        {items.slice(0, 12).map((item, index) => (
          <li key={`${title}-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function DownloadBadge({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 font-mono text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </span>
  );
}

export function BusinessFlowPanel({
  projectId,
  canStart,
  onOpenApprovals,
  loader = api,
}: {
  projectId: string;
  canStart: boolean;
  onOpenApprovals?: () => void;
  loader?: BusinessFlowLoader;
}) {
  const [catalogue, setCatalogue] = useState<BusinessCatalogueResponse | null>(null);
  const [runs, setRuns] = useState<Job[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [run, setRun] = useState<BusinessRunView | null>(null);
  const [loading, setLoading] = useState(true);
  const [runLoading, setRunLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [runError, setRunError] = useState<unknown>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelSent, setCancelSent] = useState<string | null>(null);
  const [goal, setGoal] = useState(
    "Pripremi provjerenu sljedeću poslovnu radnju za sintetičku priliku.",
  );
  const [scenario, setScenario] = useState<"research_gap" | "current_evidence">(
    "research_gap",
  );
  const catalogueGeneration = useRef(0);
  const runGeneration = useRef(0);
  const startGeneration = useRef(0);
  const runsRef = useRef<Job[]>([]);
  runsRef.current = runs;

  // A selected job belongs to the project it was selected in. Deriving the active
  // selection prevents one render from requesting the old job under a new project.
  const activeSelectedId = selectedProjectId === projectId ? selectedId : null;
  const activeJobStatus = runs.find((item) => item.id === activeSelectedId)?.status;
  const jobFinishedWithoutApproval = activeJobStatus === "failed" || activeJobStatus === "cancelled" || activeJobStatus === "killed";
  const activeSelectionRef = useRef<string | null>(null);
  activeSelectionRef.current = activeSelectedId;

  const selectRun = useCallback((jobId: string) => {
    // Fence synchronously on a real selection change, before the passive effect
    // cleanup runs, so an old request cannot resolve into the new selection.
    if (jobId !== activeSelectionRef.current) runGeneration.current += 1;
    setSelectedId(jobId);
    setSelectedProjectId(projectId);
  }, [projectId]);

  const clearProjectData = useCallback(() => {
    setCatalogue(null);
    setRuns([]);
    setSelectedId(null);
    setSelectedProjectId(null);
    setRun(null);
    setRunLoading(false);
    setError(null);
    setRunError(null);
  }, []);

  const loadCatalogueAndRuns = useCallback(async () => {
    const current = ++catalogueGeneration.current;
    // Fence a pending detail request immediately on refresh/project reload; the
    // detail effect's cleanup is not the only ordering barrier.
    runGeneration.current += 1;
    setLoading(true);
    setError(null);
    // Clear before the first await: a project switch must not leave one frame of the
    // previous project's catalogue/run on screen.
    clearProjectData();
    try {
      const [rawCatalogue, listing] = await Promise.all([
        loader.getBusinessCatalogue(projectId),
        loader.listBusinessRuns(projectId),
      ]);
      if (current !== catalogueGeneration.current) return;
      const parsed = parseBusinessCatalogueResponse(rawCatalogue);
      if (!parsed) throw new Error("catalogue_contract_invalid");
      setCatalogue(parsed);
      const items = listing.items.filter(
        (item) => item.workflow === "megabooker.business_opportunity",
      );
      setRuns(items);
      if (items.length > 0) {
        selectRun(items[0].id);
      }
    } catch (err) {
      if (current === catalogueGeneration.current) setError(err);
    } finally {
      if (current === catalogueGeneration.current) setLoading(false);
    }
  }, [projectId, loader, clearProjectData, selectRun]);

  useEffect(() => {
    void loadCatalogueAndRuns();
    return () => {
      catalogueGeneration.current += 1;
      runGeneration.current += 1;
      startGeneration.current += 1;
    };
  }, [loadCatalogueAndRuns]);

  // A project switch must not leave the previous project's start request spinning.
  useEffect(() => () => {
    startGeneration.current += 1;
    setBusy(false);
  }, [projectId]);

  // A sent cancellation belongs to one job in one project only.
  useEffect(() => {
    setCancelSent(null);
    setCancelBusy(false);
  }, [projectId, activeSelectedId]);

  useEffect(() => {
    if (!activeSelectedId) {
      setRun(null);
      setRunError(null);
      setRunLoading(false);
      return;
    }
    const jobId = activeSelectedId;
    const current = ++runGeneration.current;
    let active = true;
    let inFlight = false;
    let lastStatus = runsRef.current.find((item) => item.id === jobId)?.status ?? null;
    let timer: number | null = null;

    setRunLoading(true);
    setRunError(null);
    setRun(null);

    const isCurrent = () => active && current === runGeneration.current;

    const schedulePoll = () => {
      if (!isCurrent() || timer !== null || TERMINAL.has(lastStatus ?? "")) return;
      timer = window.setTimeout(() => {
        timer = null;
        void loadDetail();
      }, 1500);
    };

    const loadDetail = async () => {
      if (!isCurrent() || inFlight) return;
      inFlight = true;
      try {
        const detail = await loader.getBusinessRun(projectId, jobId);
        // Completion/error fencing: every await is rechecked against this identity's
        // generation. A stale initial or poll request can never write newer state.
        if (!isCurrent()) return;
        if (!detail || typeof detail !== "object"
            || !detail.job || detail.job.id !== jobId
            || detail.job.project_id !== projectId) {
          throw new Error("run_identity_mismatch");
        }
        // A terminal job stops polling even if the run projection is malformed.
        lastStatus = detail.job.status;
        const parsed = parseBusinessRunView(detail.business);
        if (!parsed) throw new Error("run_contract_invalid");
        if (parsed.job_id !== jobId) throw new Error("run_identity_mismatch");
        if (parsed.available && parsed.project_id !== projectId) {
          throw new Error("project_mismatch");
        }
        setRun(parsed);
        setRuns((previous) => previous.map((item) => (
          item.id === detail.job.id ? detail.job : item
        )));
        setRunError(null);
        setRunLoading(false);
        schedulePoll();
      } catch (err) {
        if (!isCurrent()) return;
        setRunLoading(false);
        setRunError(err);
        // Transient failures may retry, but only on the same serial identity and
        // never after a terminal status is already known.
        schedulePoll();
      } finally {
        inFlight = false;
      }
    };

    void loadDetail();

    return () => {
      active = false;
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };
  }, [projectId, activeSelectedId, loader]);

  const start = async () => {
    const current = ++catalogueGeneration.current;
    const currentStart = ++startGeneration.current;
    setLoading(false);
    setBusy(true);
    setError(null);
    try {
      const created = await loader.startBusinessRun(projectId, {
        goal,
        scenario_id: scenario,
        idempotency_key: `business-${Date.now()}-${Math.floor(Math.random() * 10_000)}`,
      });
      if (current !== catalogueGeneration.current) return;
      selectRun(created.id);
      const listing = await loader.listBusinessRuns(projectId);
      if (current !== catalogueGeneration.current) return;
      setRuns(listing.items.filter(
        (item) => item.workflow === "megabooker.business_opportunity",
      ));
    } catch (err) {
      if (current === catalogueGeneration.current) setError(err);
    } finally {
      if (currentStart === startGeneration.current) setBusy(false);
    }
  };

  const connected = catalogue?.live_connection_verified === true;

  const cancelActiveRun = async () => {
    if (!activeSelectedId) return;
    const jobId = activeSelectedId;
    setCancelBusy(true);
    try {
      await loader.cancelJob(projectId, jobId);
      setCancelSent(jobId);
    } catch (err) {
      setRunError(err);
    } finally {
      setCancelBusy(false);
    }
  };

  if (loading && !catalogue) {
    return (
      <div className="rounded border border-border p-6 text-sm text-muted-foreground"
           data-testid="business-loading">
        Učitavanje poslovnog kataloga i konteksta…
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-5 [overflow-wrap:anywhere]" data-testid="business-flow-panel">
      <div className="flex flex-wrap items-start justify-between gap-3"
           data-testid="business-header">
        <div>
          <h2 className="text-lg font-semibold">Poslovni tokovi</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {connected
              ? `Bigin: ${catalogue.integration.crm.live_connection} · Zia: ${catalogue.integration.zia.availability}`
              : "Bigin: nije povezano · Zia: nije povezano"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DownloadBadge label="način" value="Probni način" />
          <button
            type="button"
            onClick={() => void loadCatalogueAndRuns()}
            disabled={loading || busy}
            className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-50"
            data-testid="business-refresh"
          >
            Osvježi
          </button>
        </div>
      </div>

      <section className="rounded border border-border p-4" aria-labelledby="business-start-heading">
        <h3 id="business-start-heading" className="font-semibold">Pokreni probni scenarij</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Probni scenarij je lokalni izračun: košta 0 USD, ne šalje ništa vani i ne
          mijenja CRM. Rezultat je jedan prijedlog sljedećeg koraka koji vi odobravate.
        </p>
        <details data-testid="business-example" open className="mt-3 rounded border border-border/70 p-3">
          <summary className="cursor-pointer text-sm font-semibold">Primjer cilja (možete sakriti)</summary>
          <p className="mt-2 text-sm">{EXAMPLE_GOAL}</p>
          <button
            type="button"
            data-testid="business-use-example"
            className="mt-2 rounded border border-border px-3 py-1.5 text-sm"
            onClick={() => setGoal(EXAMPLE_GOAL)}
          >
            Koristi primjer
          </button>
        </details>
        <div className="mt-3 grid gap-3 md:grid-cols-[1fr_220px_auto] md:items-end">
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Poslovni cilj</span>
            <textarea
              value={goal}
              onChange={(event) => setGoal(event.target.value)}
              maxLength={500}
              rows={3}
              className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
              data-testid="business-goal"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Scenarij</span>
            <select
              value={scenario}
              onChange={(event) => setScenario(event.target.value as typeof scenario)}
              className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
              data-testid="business-scenario"
            >
              <option value="research_gap">research_gap · dokaz nedostaje</option>
              <option value="current_evidence">current_evidence · research se preskače</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => void start()}
            disabled={!canStart || busy || goal.trim().length < 10}
            className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
            data-testid="business-start"
          >
            {busy ? "Pokrećem…" : "Pokreni tok"}
          </button>
        </div>
      </section>

      <details data-testid="business-howto" className="rounded border border-border bg-muted/30 p-4">
        <summary className="cursor-pointer font-semibold">Kako se koristi</summary>
        <p className="mt-2 text-sm">
          Ovdje agenti rade poslovni tok za projekt: prikupe kontekst, provjere podatke
          i predlože jedan sljedeći korak koji vi odobrite. Zasad radi u probnom načinu:
          nema stvarnih poziva prema CRM-u ni Zii.
        </p>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm">
          <li>Pogledajte dolje što je spojeno (Bigin, Zia) i što agent smije raditi.</li>
          <li>Upišite poslovni cilj i pokrenite probni scenarij.</li>
          <li>Pregledajte prijedlog i odobrite ili odbijte u Odlukama.</li>
        </ol>
      </details>

      {error !== null && (
        <p role="alert" aria-live="polite"
           className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
           data-testid="business-error">
          {errorText(error)}
        </p>
      )}

      {(() => {
        if (!run || !run.available) return null;
        const activeJob = runs.find((item) => item.id === activeSelectedId);
        const status = activeJob?.status;
        const cancelRequested = !!(activeJob?.cancel_requested_at
          && !TERMINAL.has(activeJob.status))
          || (cancelSent !== null && cancelSent === activeSelectedId);
        const activePhase = status === "queued" || status === "running";
        // The job status is authoritative. A budget approval request must win over
        // a merely `proposed` proposal, otherwise the user is told to approve it
        // in Odluke where it is not shown at all.
        const awaitingBudget = !activePhase && status === "awaiting_budget_approval";
        // An approval hint (and its "Otvori Odluke"/"Otkaži tok" buttons) is only
        // honest while the job can still move: a terminal job is done, whatever a
        // leftover `proposed` proposal says.
        const awaitingApproval = !activePhase && !awaitingBudget
          && !TERMINAL.has(status ?? "")
          && (status === "awaiting_approval" || run.proposal?.status === "proposed");
        return (
          <section data-testid="business-run-summary" aria-live="polite"
                   className="rounded border border-primary/40 bg-primary/5 p-4">
            <h3 className="font-semibold">Što se događa</h3>
            <p className="mt-1 text-sm">
              {cancelRequested && (
                "Otkazivanje je zatraženo. Tok će se zaustaviti za nekoliko sekundi."
              )}
              {!cancelRequested && activePhase && "Agenti rade na vašem cilju. Stranica se sama osvježava."}
              {!cancelRequested && awaitingApproval && (
                "Čeka vaše odobrenje. Pregledajte prijedlog ispod i odobrite ili odbijte u Odlukama."
              )}
              {!cancelRequested && awaitingBudget && (
                "Čeka odobrenje budžeta. Ovo odobrenje se trenutno ne prikazuje u Odlukama; ako ga ne želite, otkažite tok."
              )}
              {!cancelRequested && !activePhase && !awaitingApproval && status === "needs_review" && (
                "Potreban je vaš pregled rezultata."
              )}
              {!cancelRequested && !activePhase && !awaitingApproval && status === "succeeded" && (
                <>
                  Gotovo.
                  {run.receipt?.human_next_step
                    ? ` Vaš sljedeći korak: ${run.receipt.human_next_step.title}.`
                    : " Nema daljnjih koraka."}
                </>
              )}
              {!cancelRequested && !activePhase && !awaitingBudget && !awaitingApproval
                && (status === "failed" || status === "cancelled" || status === "killed") && (
                `Nije dovršeno (${businessJobStatusLabel(status)}). Možete pokrenuti scenarij ponovno.`
              )}
            </p>
            {!cancelRequested && awaitingApproval && onOpenApprovals && (
              <button type="button" onClick={onOpenApprovals}
                      className="mt-2 rounded border border-border px-3 py-1.5 text-sm"
                      data-testid="business-summary-open-approvals">
                Otvori Odluke
              </button>
            )}
            {!cancelRequested && canStart && (awaitingApproval || awaitingBudget) && (
              <button type="button" data-testid="business-cancel-run"
                      className="mt-2 rounded border border-border px-3 py-1.5 text-sm"
                      disabled={cancelBusy}
                      onClick={() => void cancelActiveRun()}>
                Otkaži tok
              </button>
            )}
          </section>
        );
      })()}

      <div className="grid min-w-0 gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
        <section className="rounded border border-border p-3" aria-labelledby="business-runs-heading">
          <h3 id="business-runs-heading" className="font-semibold">Pokretanja</h3>
          {runs.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground" data-testid="business-runs-empty">
              Nema pokretanja za ovaj projekt.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {runs.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => selectRun(item.id)}
                    className={`w-full rounded border px-2 py-2 text-left text-xs ${
                      item.id === activeSelectedId ? "border-primary" : "border-border"
                    }`}
                    data-testid={`business-run-${item.id}`}
                  >
                    <span className="block font-mono">{item.id}</span>
                    <span className="mt-1 block">{businessJobStatusLabel(item.status)}</span>
                    <span className="mt-0.5 block text-muted-foreground">
                      {item.created_at ? new Date(item.created_at).toLocaleString("hr-HR") : "—"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="min-w-0 space-y-4" aria-live="polite">
          {runError !== null && (
            <p role="alert" className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
               data-testid="business-run-error">
              {errorText(runError)}
            </p>
          )}
          {runLoading && (
            <p className="text-sm text-muted-foreground" data-testid="business-run-loading">
              Učitavanje odabranog toka…
            </p>
          )}
          {!runLoading && !activeSelectedId && (
            <p className="rounded border border-border p-4 text-sm text-muted-foreground">
              Odaberite pokretanje ili pokrenite novi sintetički scenarij.
            </p>
          )}
          {run && !run.available && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 p-4 text-sm"
                 data-testid="business-run-unavailable">
              <p className="font-semibold">Tok nije dostupan</p>
              <p className="mt-1">{run.reason ?? "unavailable"}</p>
            </div>
          )}
          {run && run.available && (
            <>
              <div className="rounded border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">Tijek izvršenja</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{run.goal}</p>
                  </div>
                </div>
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-muted-foreground">{TECH_DETAILS}</summary>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <DownloadBadge label="job" value={run.job_id} />
                    {Object.entries(run.states ?? {}).filter(([, value]) => value).map(([key]) => (
                      <span key={key} className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5">
                        {key}
                      </span>
                    ))}
                    {run.synthetic_only && (
                      <span className="rounded border border-border px-2 py-0.5">synthetic_only</span>
                    )}
                  </div>
                </details>
                <ol className="mt-4 space-y-2">
                  {(run.steps ?? []).map((step) => (
                    <li key={`${step.index}-${step.step_id}`}
                        className="rounded border border-border p-3"
                        data-testid={`business-step-${step.step_id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold">
                            {step.index + 1}. {businessStepLabel(step.step_id)}
                          </p>
                        </div>
                        <span className={`rounded border px-2 py-0.5 text-[11px] ${statusClass(step.status)}`}>
                          {stepStatusLabel(step.status)}
                        </span>
                      </div>
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs text-muted-foreground">{TECH_DETAILS}</summary>
                        <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                          {step.role ?? "role?"} · {step.agent_key ?? "agent?"}
                        </p>
                      </details>
                      {step.dependency_reason && (
                        <p className="mt-1 text-xs text-muted-foreground">{step.dependency_reason}</p>
                      )}
                      {step.step_id === "research"
                        && typeof step.summary.reason === "string" && (
                        <p className="mt-1 text-xs">
                          Preskočeno/izvršeno: {step.summary.reason}
                        </p>
                      )}
                      {step.step_id === "research" && Array.isArray(step.summary.sources)
                        && step.summary.sources.length > 0 && (
                        <ul className="mt-1 space-y-1 text-xs">
                          {(step.summary.sources as Array<Record<string, unknown>>).map((source, index) => {
                            const url = safeBusinessSourceUrl(source.url);
                            if (!url) return null;
                            return (
                              <li key={`source-${index}`}>
                                <a className="underline" href={url} target="_blank"
                                   rel="noreferrer noopener">
                                  {String(source.title ?? url)}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {(step.step_id === "review" || step.step_id === "sales_next_action") && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          source vs inference:{" "}
                          {step.source_vs_inference?.source.length ?? 0} /{" "}
                          {step.source_vs_inference?.inference.length ?? 0}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              </div>

              {run.proposal && (
                <div className="rounded border border-border p-4" data-testid="business-proposal">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h3 className="font-semibold">Prijedlog i ljudsko odobrenje</h3>
                    <span className={`rounded border px-2 py-0.5 text-[11px] ${statusClass(run.proposal.status)}`}>
                      {run.proposal.status}
                    </span>
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground">{TECH_DETAILS}</summary>
                    <dl className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
                      <div><dt className="text-muted-foreground">action</dt><dd className="font-mono">{run.proposal.action_type}</dd></div>
                      <div><dt className="text-muted-foreground">risk</dt><dd>{run.proposal.risk}</dd></div>
                      <div><dt className="text-muted-foreground">content_hash</dt><dd className="break-all font-mono">{run.proposal.content_hash}</dd></div>
                      <div><dt className="text-muted-foreground">source_snapshot_hash</dt><dd className="break-all font-mono">{run.proposal.source_snapshot_hash}</dd></div>
                      <div><dt className="text-muted-foreground">target</dt><dd className="font-mono">{run.proposal.target_ref.resource}</dd></div>
                      <div><dt className="text-muted-foreground">before</dt><dd className="font-mono">{JSON.stringify(run.proposal.before)}</dd></div>
                    </dl>
                    {run.proposal.after && (
                      <div className="mt-2">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">after</p>
                        <pre className="mt-1 overflow-x-auto rounded bg-muted/40 p-2 text-[11px]">
                          {JSON.stringify(run.proposal.after, null, 2)}
                        </pre>
                      </div>
                    )}
                  </details>
                  {run.proposal.invalidated_reason && (
                    <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                      Prijedlog je invalidiran: {run.proposal.invalidated_reason}. Stari
                      prijedlog se ne izvršava.
                    </p>
                  )}
                  {onOpenApprovals && !jobFinishedWithoutApproval && (run.proposal.status === "proposed" || run.proposal.status === "approved") && (
                    <button type="button" onClick={onOpenApprovals}
                            className="mt-3 rounded border border-border px-3 py-1.5 text-sm"
                            data-testid="business-open-approvals">
                      Otvori postojeća Odobrenja
                    </button>
                  )}
                </div>
              )}

              {run.receipt && (
                <div className="rounded border border-border p-4" data-testid="business-receipt">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h3 className="font-semibold">Potvrda i proizvedeni efekt</h3>
                    <span className={`rounded border px-2 py-0.5 text-[11px] ${statusClass(run.receipt.status)}`}>
                      {run.receipt.status}
                    </span>
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground">{TECH_DETAILS}</summary>
                    <dl className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
                      <div><dt className="text-muted-foreground">external_id</dt><dd className="font-mono">{run.receipt.external_id ?? "—"}</dd></div>
                      <div><dt className="text-muted-foreground">effect</dt><dd>{run.receipt.effect ?? "—"}</dd></div>
                      <div><dt className="text-muted-foreground">resource</dt><dd>{run.receipt.target_resource ?? "—"}</dd></div>
                      <div><dt className="text-muted-foreground">readback</dt><dd>{run.receipt.readback?.synthetic ? "synthetic record confirmed" : "not confirmed"}</dd></div>
                      <div><dt className="text-muted-foreground">cost</dt><dd>{formatMicros(run.receipt.cost?.amount_micros ?? null)} · {run.receipt.cost?.cost_scope ?? "unknown"}</dd></div>
                      <div><dt className="text-muted-foreground">live provider cost</dt><dd>{run.receipt.cost?.live_provider_cost ?? "unknown"}</dd></div>
                    </dl>
                  </details>
                  {run.receipt.human_next_step && (
                    <div className="mt-3 rounded border border-border/70 p-3 text-sm">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Sljedeći ljudski zadatak</p>
                      <p className="mt-1 font-semibold">{run.receipt.human_next_step.title}</p>
                      <p className="text-xs text-muted-foreground">
                        vlasnik: {run.receipt.human_next_step.owner_role} · rok: {run.receipt.human_next_step.due_in_days} d
                      </p>
                      {run.receipt.human_next_step.instruction && (
                        <p className="mt-1 text-xs">{run.receipt.human_next_step.instruction}</p>
                      )}
                    </div>
                  )}
                  {run.receipt.business_summary && (
                    <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                      <SummaryList title="Prihvaćeno" items={run.receipt.business_summary.accepted ?? []} />
                      <SummaryList title="Nije prihvaćeno" items={run.receipt.business_summary.not_accepted ?? []} />
                    </div>
                  )}
                </div>
              )}

              {!run.proposal && !run.receipt && (
                <p className="rounded border border-border p-4 text-sm text-muted-foreground"
                   data-testid="business-waiting">
                  Tok još nema prijedlog ni potvrdu. Pokrenite radnika ili pričekajte
                  ljudsko odobrenje; tekst agenta nije CRM efekt.
                </p>
              )}
            </>
          )}
        </section>
      </div>

      <details data-testid="business-background" className="rounded border border-border p-4">
        <summary className="cursor-pointer font-semibold">
          Pozadina: Bigin, Zia, uloge i projektni kontekst
        </summary>
        <div className="mt-4 space-y-5">
          {catalogue && (
            <section className="rounded border border-border p-4" aria-labelledby="business-context-heading">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 id="business-context-heading" className="font-semibold">Projektni kontekst</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {catalogue.project_context.product_summary}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <DownloadBadge label="program" value={catalogue.project_context.program} />
                  <DownloadBadge label="verzija" value={String(catalogue.project_context.version)} />
                  <DownloadBadge label="sustav" value={catalogue.project_context.system_of_record} />
                </div>
              </div>
              <details className="mt-3" data-testid="business-context-details">
                <summary className="cursor-pointer text-xs text-muted-foreground">{TECH_DETAILS}</summary>
                <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Idealni kupac</p>
                    <p>{catalogue.project_context.ideal_customer}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Vlasnik odluke</p>
                    <p>{catalogue.project_context.decision_owner_role}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Odobreni cjenik: nije potvrđen (ne prikazuje se kao nula).
                    </p>
                  </div>
                  <SummaryList title="Dopuštene tvrdnje" items={catalogue.project_context.allowed_claims} />
                  <SummaryList title="Nedopuštene tvrdnje" items={catalogue.project_context.disallowed_claims} />
                </div>
                <p className="mt-3 rounded border border-border/70 bg-muted/30 p-2 text-xs text-muted-foreground">
                  {catalogue.project_context.access_note}
                </p>
              </details>
            </section>
          )}

          {catalogue && (
            <section className="grid gap-3 md:grid-cols-2" aria-label="Integracijski status">
              <div className="rounded border border-border p-4" data-testid="business-crm-status">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold">Bigin (CRM)</h3>
                  <span className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs">
                    {connected ? catalogue.integration.crm.live_connection : NOT_CONNECTED}
                  </span>
                </div>
                {!connected && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Evidencija kontakata i prilika. Za povezivanje treba Zoho pristup i provjera računa.
                  </p>
                )}
                <details className="mt-3" data-testid="business-crm-details">
                  <summary className="cursor-pointer text-xs text-muted-foreground">{TECH_DETAILS}</summary>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {catalogue.integration.crm.provider} · veza nije potvrđena · trošak{" "}
                    {catalogue.integration.crm.cost.status} · {catalogue.integration.crm.live_connection}
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                    {catalogue.integration.crm.object_mapping.map((row) => (
                      <li key={row.object}>{row.object}: {row.status}</li>
                    ))}
                  </ul>
                </details>
              </div>
              <div className="rounded border border-border p-4" data-testid="business-zia-status">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold">Zia</h3>
                  <span className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs">
                    {connected ? catalogue.integration.zia.availability : NOT_CONNECTED}
                  </span>
                </div>
                {!connected && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Zoho AI asistent. Za povezivanje treba potvrda da je dostupan u vašoj organizaciji i Zoho pristup.
                  </p>
                )}
                <details className="mt-3" data-testid="business-zia-details">
                  <summary className="cursor-pointer text-xs text-muted-foreground">{TECH_DETAILS}</summary>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Org / agent / verzija: nije potvrđeno. Alati: {catalogue.integration.zia.tools.length}.
                    Trošak: {catalogue.integration.zia.cost_status}. Read-only poziv nije dopušten.
                    Dostupnost: {catalogue.integration.zia.availability}.
                  </p>
                  <p className="mt-2 text-xs">{catalogue.integration.zia.reason}</p>
                </details>
              </div>
            </section>
          )}

          {catalogue && (
            <section className="rounded border border-border p-4" aria-labelledby="business-roles-heading">
              <h3 id="business-roles-heading" className="font-semibold">Katalog poslovnih uloga</h3>
              <details className="mt-2" data-testid="business-roles-details">
                <summary className="cursor-pointer text-xs text-muted-foreground">{TECH_DETAILS}</summary>
                <p className="mt-2 text-sm text-muted-foreground">
                  Samo determinističke sintetičke uloge su uključene. Planirane uloge nemaju
                  runtime identitet i ne mogu se odabrati.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {catalogue.catalogue.roles.map((role) => (
                    <article key={role.planning_role_id} className="rounded border border-border p-3"
                             data-testid={`business-role-${role.planning_role_id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold">{role.name}</h4>
                        <span className={`rounded border px-2 py-0.5 text-[11px] ${
                          role.implementation_status === "implemented_synthetic"
                            ? "border-emerald-500/40 bg-emerald-500/10"
                            : "border-border text-muted-foreground"
                        }`}>
                          {businessRoleStatusLabel(role.implementation_status)}
                        </span>
                      </div>
                      <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                        {role.runtime_identity
                          ? `${role.runtime_identity.agent_key} v${role.runtime_identity.agent_version}`
                          : "bez runtime identiteta"}
                      </p>
                      <p className="mt-1 text-xs">Vlasnik odluke: {role.decision_owner}</p>
                      {role.runtime_identity && (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          paid={String(role.runtime_identity.paid_agent)} · zia={String(role.runtime_identity.zia_agent)}
                        </p>
                      )}
                      {role.availability_reason && (
                        <p className="mt-1 text-xs text-muted-foreground">{role.availability_reason}</p>
                      )}
                    </article>
                  ))}
                </div>
              </details>
            </section>
          )}
        </div>
      </details>
    </div>
  );
}
